import React, { useEffect, useState, ReactNode } from 'react'
import Text from '../../atoms/Text/Text'

interface MobileDetectionWrapperProps {
  children: ReactNode
  disabled?: boolean
}

type DeviceType = 'mobile' | 'tablet' | 'valid_device'

export const isMobileDevice = (): boolean => {
  const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
  const isSmallScreen = window.screen.width < 768 || window.screen.height < 768
  return isSmallScreen && hasTouch
}

export const MobileDetectionWrapper: React.FC<MobileDetectionWrapperProps> = ({
  children,
  disabled = false
}) => {
  const [deviceType, setDeviceType] = useState<DeviceType>('valid_device')

  // Function for device detection (moved outside useEffect for reuse)
  const detectDeviceType = (): DeviceType => {
    const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0
    const isSmallWindowSize = window.innerWidth < 1024 || window.innerHeight < 768
    const isSmallScreen = window.screen.width < 768 || window.screen.height < 768

    if (isMobileDevice()) {
      return 'mobile'
    }

    // Tablet detection - any device with touch that's not a small screen or has touch and it's window size is to small
    if ((hasTouch && !isSmallScreen) || (hasTouch && isSmallWindowSize)) {
      return 'tablet'
    }

    return 'valid_device'
  }

  useEffect(() => {
    // Initial detection
    const initialType = detectDeviceType()
    setDeviceType(initialType)

    // Handle resize events
    const handleResize = () => {
      const currentType = detectDeviceType()
      // Only update state if the device type changes
      if (currentType !== deviceType) {
        setDeviceType(currentType)
      }
    }

    // Add resize listener
    window.addEventListener('resize', handleResize)

    // Clean up listener
    return () => window.removeEventListener('resize', handleResize)
  }, [deviceType])

  const shouldShowRestriction = deviceType === 'mobile' && !disabled

  if (shouldShowRestriction) {
    return (
      <div data-testid='mobile-restriction-container' className='bg-black flex justify-center items-center min-h-screen w-full p-4'>
        <div className='glow-effect max-w-md w-full'>
          <div className='diamond-corner p-1 bg-dopage-lightGreen w-full'>
            <div className='inner bg-black'>
              <div className='py-8 px-4 flex flex-col justify-center items-center text-dopage-darkGreen'>
                <h2 data-testid='mobile-restriction-title' className='text-xl md:text-2xl font-bold mb-6 w-full text-center'>
                  <Text
                    className='font-bold text-center w-full'
                    bold
                    size='LG'
                    text='Grab your computer!'
                  />
                </h2>
                <p data-testid='mobile-restriction-message' className='text-base md:text-xl text-center text-dopage-lightGreen mb-4 w-full'>
                  <Text
                    className='text-center w-full'
                    size='SM'
                    text='The DopaGE Overdose Prevention Portal is not accessible via mobile device. Please sign in from a laptop or desktop computer.'
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}
